import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import type { RateView } from '@/types/product';
import { groupRates } from './rateGroups';
import { money } from './workbenchPricing';

interface Props {
  rates: RateView[];
  changedIds?: Set<string>;
  renderRate: (rate: RateView) => ReactNode;
}
export default function GroupedRates({ rates, changedIds, renderRate }: Props) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const groups = groupRates(rates, search);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="relative mx-4 my-3 shrink-0">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          aria-label="Uurtarieven zoeken"
          placeholder="Zoek werkzaamheid of groep"
          className="h-9 pl-8 text-sm"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      <div className="min-h-0 max-h-[min(60vh,640px)] flex-1 overflow-y-auto overscroll-contain">
        {groups.map((group) => {
          const open = Boolean(search.trim()) || expanded.has(group.name);
          const changed = group.rates.filter((rate) => changedIds?.has(rate.id)).length;
          const amounts = group.rates
            .map((rate) => rate.currentCents)
            .filter((value): value is number => value !== null);
          const low = amounts.length ? Math.min(...amounts) : null;
          const high = amounts.length ? Math.max(...amounts) : null;
          return (
            <section key={group.name} aria-label={`${group.name} tarieven`} className="border-t">
              <button
                type="button"
                className="flex w-full items-center gap-2 bg-muted/50 px-4 py-3 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-info"
                aria-expanded={open}
                onClick={() =>
                  setExpanded((current) => {
                    const next = new Set(current);
                    if (next.has(group.name)) next.delete(group.name);
                    else next.add(group.name);
                    return next;
                  })
                }
              >
                {open ? (
                  <ChevronDown className="h-4 w-4 shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{group.name}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {group.rates.length} {group.rates.length === 1 ? 'tarief' : 'tarieven'}
                    {low !== null
                      ? ` · ${money(low)}${high !== low ? ` – ${money(high)}` : ''}`
                      : ''}
                  </span>
                </span>
                {changed > 0 && (
                  <span className="shrink-0 text-xs font-semibold text-info">
                    {changed} gewijzigd
                  </span>
                )}
              </button>
              {open && (
                <div className="divide-y px-4">
                  {group.name === 'Bestaande tarieven' && (
                    <p className="py-3 text-xs leading-5 text-muted-foreground">
                      Gedeelde tarieven van eerdere imports. Kies bij een productregel een
                      werkzaamheid om die apart te beheren.
                    </p>
                  )}
                  {group.rates.map((rate) => (
                    <div key={rate.id}>{renderRate(rate)}</div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
        {!groups.length && (
          <div className="px-4 py-6 text-sm text-muted-foreground">
            Geen tarieven gevonden.
            {search && (
              <button
                type="button"
                className="ml-1 font-semibold text-info hover:underline"
                onClick={() => setSearch('')}
              >
                Zoekopdracht wissen
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
